import Stopwatch from "../../../utils/Stopwatch";
import React from "react";
import {
  CtaOverrideEntries,
  CtaOverrideEntry,
} from "../../../build/product-builder-ctas/ctaOverrideTypes";
import { ExperimentDefinition, MerchandisingProductView } from "../../../types/ContentfulTypes";
import TrackingUtil from "../../../utils/tracking";
import { Logger } from "../../../logging/logger";
import { LowestPrice } from "@vp/hamilton-service-clients/dist/src/types/Pricing";
import { ProductBuilderOptions } from "../../../types/ProductBuilderTypes";
import { PluginData } from "../../../pluginsInfrastructure/PluginRegistryTypes";
import { Icon } from "@vp/swan";
import { CloudinaryIcon } from "../../../components/CloudinaryIcon";
import RangeType from "../../../types/RangeTypes";

interface CtaDisplayInfo {
  actualCtaText: string;
  actualCtaDescription: string;
  actualCtaIcon: React.ReactNode;
}
export const getProductCtaInfo = (
  pluginData: PluginData,
  ctaOverrideEntries: CtaOverrideEntries,
  flowType: string,
  isDesignUploaded: boolean,
  isPrimaryCta: boolean
): CtaDisplayInfo => {
  const defaultCtaText = getDictionaryEntryValue("ctaText");
  const defaultCtaDescription = getDictionaryEntryValue("ctaDescription");
  const defaultCtaIcon = ctaIcons[flowType] ? (
    <Icon iconType={ctaIcons[flowType]} size="20p" />
  ) : (
    undefined
  );

  const ctaOverride = ctaOverrideEntries?.[flowType];
  const ctaDisplayInfo = getOverridenCtaDisplayInfo(
    isPrimaryCta,
    ctaOverride,
    defaultCtaText,
    defaultCtaDescription,
    defaultCtaIcon
  );
  let actualCtaText = ctaDisplayInfo.ctaText;
  let actualCtaDescription = ctaDisplayInfo.ctaDescription;

  if (isDesignUploaded) {
    actualCtaText = getDictionaryEntryValue("uploadedDesignCtaText") || actualCtaText;
    actualCtaDescription =
      getDictionaryEntryValue("uploadedDesignCtaDescription") || actualCtaDescription;
  }
  return { actualCtaText, actualCtaDescription, actualCtaIcon: ctaDisplayInfo.ctaIcon };

  function getDictionaryEntryValue(key: string): string {
    return pluginData.contentful[key] || "";
  }
};

const getOverridenCtaDisplayInfo = (
  isPrimaryCta: boolean,
  ctaOverrideEntry?: CtaOverrideEntry,
  defaultCtaText?: string,
  defaultCtaDescription?: string,
  defaultCtaIcon?: React.ReactNode
) => {
  const overridenIcon = ctaOverrideEntry?.icon ? (
    <CloudinaryIcon
      image={ctaOverrideEntry.icon}
      variant={isPrimaryCta ? "primary" : "secondary"}
    />
  ) : (
    defaultCtaIcon
  );
  return {
    ctaText: ctaOverrideEntry?.text?.value || defaultCtaText || "",
    ctaDescription: ctaOverrideEntry?.disableCtaDescription
      ? ""
      : ctaOverrideEntry?.description?.value || defaultCtaDescription || "",
    ctaIcon: ctaOverrideEntry?.disableCtaIcon ? undefined : overridenIcon,
  };
};

export interface ExperimentDetails {
  name: string;
  detail?: string;
}

export const getCtaOnClickHandler = async (props: {
  event: React.MouseEvent<HTMLElement, MouseEvent>;
  mpv: MerchandisingProductView;
  selections: Record<string, string>;
  initialSelections: Record<string, string>;
  pageSection: string;
  position: number;
  url: string;
  linkType: string;
  flowContexts: ExperimentDefinition[];
  initialQuantity: number;
  selectedQuantity: number;
  prices: Record<string, LowestPrice>;
  productBuilderOptions: ProductBuilderOptions;
  initialPrices: Record<string, LowestPrice> | undefined;
  isDesignUploaded: boolean;
  compatibleRanges?: Record<string, RangeType[]>;
}) => {
  const {
    mpv,
    selections,
    initialSelections,
    pageSection,
    position,
    url,
    linkType,
    initialQuantity,
    selectedQuantity,
    prices,
    productBuilderOptions,
    initialPrices,
    flowContexts,
    isDesignUploaded,
    compatibleRanges,
  } = props;
  const ctaType = getTrackCtaType(linkType, isDesignUploaded);
  const stopwatch = new Stopwatch();
  try {
    TrackingUtil.trackCtaTiming(stopwatch.stop());

    flowContexts?.forEach(flowContext => {
      TrackingUtil.trackExperimentClicked(mpv, {
        label: `${pageSection} view`,
        eventDetail: url
          ? `${window.location.pathname};${url};${pageSection};${ctaType}`
          : undefined,
        navigationDetail: `${ctaType}-CTA:${position}`,
        pageSection,
        pageStage: "Discover",
        experimentName: flowContext.experiment,
        variationName: flowContext.variation,
      });
    });

    if (url) {
      TrackingUtil.trackCtaClick({
        ctaType: ctaType,
        pageSection,
        destination: url,
        mpvId: mpv.mpvId,
        position,
        selections,
      });
      TrackingUtil.trackUpdatedSelections(
        mpv.mpvId,
        mpv.name,
        mpv.coreProductId,
        selections,
        initialSelections,
        initialQuantity,
        selectedQuantity,
        prices,
        initialPrices,
        productBuilderOptions,
        compatibleRanges
      );
      return true;
    }
  } catch (ex) {
    const error = ex as Error;
    Logger.instance.error("Cannot proceed from product page: Failed to fetch", {
      linkType,
      errorStack: error.stack,
    });
    return false;
  }
};

const getTrackCtaType = (linkType: string, isDesignUploaded: boolean): string => {
  switch (linkType) {
    case "gallery": {
      return "Browse Designs";
    }
    case "albelligallery": {
      return "Get Started";
    }
    case "albellistudio": {
      return "Get Started";
    }
    case "studio": {
      return isDesignUploaded ? "Continue with your Design" : "Upload Design";
    }
    case "studio:fullbleed":
    case "studio:blank":
    case "studio:custom":
    case "studio:loadedtemplate":
    case "studio:template": {
      return "Start Designing";
    }
    case "uploadandgo": {
      return "Upload And Go";
    }
    case "uploadandprint": {
      return "Upload And Print";
    }
    case "99designs": {
      return "99designs";
    }
    case "designassistant": {
      return "Design assistant";
    }
    default:
      return "";
  }
};

const ctaIcons: Record<string, IconType> = {
  gallery: "browseDesigns",
  studio: "upload",
  "studio:blank": "edit",
  "studio:custom": "upload",
  "studio:fullbleed": "upload",
  "studio:template": "upload",
  albelligallery: "image",
  albellistudio: "image",
  uploadandgo: "upload",
  uploadandprint: "upload",
  "99designs": "image",
  designassistant: "magic",
  "studio:loadedtemplate": "edit",
};

type IconType = "browseDesigns" | "upload" | "edit" | "image" | "magic";
